import BusinessIcon from '@mui/icons-material/Business';
import WorkOutline from '@mui/icons-material/WorkOutline';
import { resolveProjectBadgeSwatch } from '@/shared/data/projectBadges';
import { r, s, typographyTokens } from '@/theme/tokens';
import { Chip } from '@/ui';
import { Box, Text } from '@/ui/primitives';
import type { AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { NotificationContextValue } from '@/providers/notificationContext';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import type { SidebarItem, SidebarSection } from '@/ui/composites/SidebarNav';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type BuildSidebarContext = {
  session: AuthSession | null;
  navItems: NavItem[];
  activePath: string;
  onNavigate: (path: string) => void;
  preloadRoute?: (path: string) => Promise<void> | null;
  featureFlags?: FeatureFlagResolveResult['flags'] | null;
  addOns?: {
    deliveryEnabled: boolean;
    operationsEnabled: boolean;
  } | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  planName: string | null;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  setSelectedOrganisationId: (value: string | null) => void;
  setSelectedProjectId: (value: string | null) => void;
  notify: NotificationContextValue['notify'];
  projectsOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'>
  >;
  allProjectsOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'>
  >;
  organisationsOptions: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject: boolean;
  lockProjectSelector: boolean;
  projectLabel: string;
  projectBadge: { chars: string; color: ProjectDto['badgeColor'] } | null;
  onAddProject: () => void;
  onAddOrganisation: () => void;
};

const isSelected = (
  itemPath: string,
  secondaryPath: string | undefined,
  selectedPath: string,
): boolean => {
  if (itemPath === '/') {
    return selectedPath === '/';
  }
  if (selectedPath.startsWith(itemPath)) {
    return true;
  }
  if (secondaryPath && selectedPath.startsWith(secondaryPath)) {
    return true;
  }
  return false;
};

const buildProjectBadge = (projectBadge: BuildSidebarContext['projectBadge']): ReactElement => (
  <Box
    sx={{
      width: s('2xl'),
      height: s('2xl'),
      borderRadius: r('sm'),
      backgroundColor: resolveProjectBadgeSwatch(projectBadge?.color ?? 'teal'),
      color: 'common.white',
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
    }}
  >
    <Text
      role="label"
      component="span"
      sx={{
        lineHeight: 1,
        fontSize: typographyTokens.captionSmall.fontSize,
        pl: s('xxs'),
        color: 'common.white',
      }}
    >
      {projectBadge?.chars ?? '?'}
    </Text>
  </Box>
);

const shouldShowProjectBadge = (
  projectLabel: string,
  projectBadge: BuildSidebarContext['projectBadge'],
): boolean => projectLabel !== 'All Projects' && Boolean(projectBadge);

const buildSidebarItem = (
  item: NavItem,
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem => ({
  id: item.key,
  label: item.label,
  href: item.path,
  icon: item.icon,
  active: isSelected(item.path, item.secondaryPath, selectedPath),
  preload: preloadRoute ? (): Promise<void> | null => preloadRoute(item.path) : undefined,
});

const buildSecondaryItem = (
  item: NavItem,
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem | null => {
  const secondaryPath = item.secondaryPath;
  if (!item.secondary || !secondaryPath) return null;
  return {
    id: `${item.key}-secondary`,
    label: item.secondary,
    href: secondaryPath,
    active: isSelected(secondaryPath, undefined, selectedPath),
    preload: preloadRoute ? (): Promise<void> | null => preloadRoute(secondaryPath) : undefined,
  };
};

const pickNavItems = (
  items: NavItem[],
  keys: string[],
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem[] => {
  const byKey = new Map(items.map((item) => [item.key, item]));
  return keys.flatMap((key) => {
    const match = byKey.get(key);
    if (!match) return [];
    const primaryItem = buildSidebarItem(match, selectedPath, preloadRoute);
    const secondaryItem = buildSecondaryItem(match, selectedPath, preloadRoute);
    return secondaryItem ? [primaryItem, secondaryItem] : [primaryItem];
  });
};

const buildWorkspaceSection = ({
  activePath,
  onNavigate,
  selectedOrganisationId,
  setSelectedOrganisationId,
  setSelectedProjectId,
  notify,
  projectsOptions,
  allProjectsOptions,
  organisationsOptions,
  canCreateProject,
  lockProjectSelector,
  projectLabel,
  projectBadge,
  onAddProject,
  onAddOrganisation,
}: BuildSidebarContext): SidebarSection => {
  const resolvedProjectsOptions = projectsOptions.length > 0 ? projectsOptions : allProjectsOptions;
  const hasMultipleOrganisations = organisationsOptions.length > 1;
  const organisationLabel = (() => {
    if (!hasMultipleOrganisations) return 'Organisation';
    if (!selectedOrganisationId) return 'Select Organisation';
    const selectedOrganisation = organisationsOptions.find(
      (org) => org.id === selectedOrganisationId,
    );
    return selectedOrganisation?.name ?? 'Organisation';
  })();
  const organisationItems = organisationsOptions.map((org) => ({
    id: org.id,
    label: org.name,
    onClick: () => {
      setSelectedOrganisationId(org.id);
      setSelectedProjectId(null);
      notify({ message: `Switched to ${org.name}`, tone: 'blue' });
      onNavigate('/execution-runs');
    },
  }));
  const projectItems: Array<{ id: string; label: string; onClick?: () => void }> = [
    {
      id: 'all-projects',
      label: 'All Projects',
      onClick: () => {
        setSelectedProjectId(null);
        notify({ message: 'Viewing all projects', tone: 'blue' });
        onNavigate('/execution-runs');
      },
    },
    ...resolvedProjectsOptions.map((project) => ({
      id: project.id,
      label: project.name,
      onClick: () => {
        setSelectedProjectId(project.id);
        notify({ message: `Switched to ${project.name}`, tone: 'blue' });
        onNavigate(activePath);
      },
    })),
  ];
  projectItems.push({
    id: 'add-project',
    label: 'Add New',
    ...(canCreateProject ? { onClick: onAddProject } : {}),
  });

  const items: SidebarItem[] = [
    ...(hasMultipleOrganisations
      ? [
          {
            id: 'workspace-organisation',
            label: organisationLabel,
            href: '#organisation',
            popover: {
              items: [
                ...organisationItems,
                {
                  id: 'add-organisation',
                  label: 'Add New Organisation',
                  onClick: onAddOrganisation,
                },
              ],
            },
          },
        ]
      : []),
    {
      id: 'workspace-project',
      label: projectLabel,
      href: '#projects',
      leadingAdornment: shouldShowProjectBadge(projectLabel, projectBadge)
        ? buildProjectBadge(projectBadge)
        : undefined,
      disabled: lockProjectSelector,
      disabledTone: 'default' as const,
      popover: lockProjectSelector
        ? undefined
        : {
            title: 'Projects',
            items: projectItems,
          },
    },
  ];

  return { title: 'Workspace', items };
};

export const buildSidebarSections = (
  context: BuildSidebarContext,
): { sections: SidebarSection[]; footerItems: SidebarItem[] } => {
  const {
    session,
    navItems,
    activePath,
    onNavigate: _onNavigate,
    preloadRoute,
    addOns,
    isAdmin,
    isSuperuser,
    planName,
    selectedOrganisationId,
    selectedProjectId,
    organisationsOptions,
    projectsOptions,
    allProjectsOptions,
    lockProjectSelector,
  } = context;
  const filteredNav = navItems.filter((item) => {
    if (item.requiresSuperuser) {
      return isSuperuser;
    }
    if (item.requiresAdmin) {
      return isAdmin;
    }
    return true;
  });
  const fallbackOrganisationId = session?.lastOrganisationId ?? session?.orgId ?? null;
  const resolvedOrganisationId = selectedOrganisationId ?? fallbackOrganisationId;
  const resolvedProjectsOptions = projectsOptions.length > 0 ? projectsOptions : allProjectsOptions;
  const selectedOrganisationSlug =
    resolvedOrganisationId === null
      ? null
      : (organisationsOptions.find((org) => org.id === resolvedOrganisationId)?.slug ?? null);
  const resolvedProjectId =
    selectedProjectId ??
    (lockProjectSelector && resolvedProjectsOptions.length === 1
      ? (resolvedProjectsOptions[0]?.id ?? null)
      : null);
  const deliveryEnabled = resolvedOrganisationId === null ? true : addOns?.deliveryEnabled === true;
  const operationsEnabled =
    resolvedOrganisationId === null ? true : addOns?.operationsEnabled === true;
  const selectedProjectSlug =
    resolvedProjectId === null
      ? null
      : (resolvedProjectsOptions.find((project) => project.id === resolvedProjectId)?.slug ??
        allProjectsOptions.find((project) => project.id === resolvedProjectId)?.slug ??
        null);
  const footerItems: SidebarItem[] = [
    ...(selectedOrganisationSlug
      ? [
          {
            id: 'organisation-settings',
            label: 'Organisation Settings',
            href: '/organisation-settings',
            icon: <BusinessIcon fontSize="small" />,
            active: isSelected('/organisation-settings', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/organisation-settings')
              : undefined,
          },
        ]
      : []),
    ...(selectedProjectSlug
      ? [
          {
            id: 'project-settings',
            label: 'Project Settings',
            href: '/project-settings',
            icon: <WorkOutline fontSize="small" />,
            active: isSelected('/project-settings', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/project-settings')
              : undefined,
          },
        ]
      : []),
    ...(selectedOrganisationSlug
      ? [
          {
            id: 'plan-settings',
            label: 'Plan Settings',
            href: '/plan-settings',
            active: isSelected('/plan-settings', undefined, activePath),
            rightAdornment: <Chip label={planName ?? 'Loading'} size="xs" tone="default" />,
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/plan-settings')
              : undefined,
          },
        ]
      : []),
    ...(isSuperuser
      ? [
          {
            id: 'allowed-users',
            label: 'Superuser',
            href: '/superuser',
            active: isSelected('/superuser', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/superuser')
              : undefined,
          },
        ]
      : []),
  ];
  const baseSections: SidebarSection[] = [
    buildWorkspaceSection(context),
    {
      title: 'Overview',
      items: pickNavItems(filteredNav, ['dashboard', 'reports'], activePath, preloadRoute),
    },
    ...(!deliveryEnabled
      ? []
      : [
          {
            title: 'Delivery',
            items: pickNavItems(filteredNav, ['releases', 'features'], activePath, preloadRoute),
          },
        ]),
    {
      title: 'Test',
      items: pickNavItems(
        filteredNav,
        ['execution-runs', 'suites', 'tests'],
        activePath,
        preloadRoute,
      ),
    },
    ...(!operationsEnabled
      ? []
      : [
          {
            title: 'Operations',
            items: pickNavItems(
              filteredNav,
              ['pipelines', 'stages', 'jobs'],
              activePath,
              preloadRoute,
            ),
          },
        ]),
  ];
  const filteredSections = baseSections.filter((section) => {
    if (section.items.length === 0) return false;
    return true;
  });
  return { sections: filteredSections, footerItems };
};
